import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { CompanyAction } from '../../actions';
import { RayForm } from '../../components/common';
import { Loading } from '../../components/common/Loading';
import { EditSaveCancelBar } from '../../components/common/Buttons';
import AdminCompanyDelete from './AdminCompanyDelete';
import IssueManagerSection from '../../components/company/IssueManagerSection';
import CompanyWorkScheduleSection from '../../components/workschedule/CompanyWorkScheduleSection';
import { Translate as t } from '../../config/Translate';
import { SettingsReturnButton } from '../user/AdminSettingShortcuts';
import { isCompanySuperUser } from '../../lib/companyPermissionGuard';
import { buildCompanyFormControls, fetchBranchUserCombo } from '../../components/company/companyFormControls';
import { useBatchEdit } from '../../hooks/useBatchEdit';

const AdminCompanyEdit = ({ company, GetCompanyInfo, UpdateCompanyInfo }) => {

    const userInfo = JSON.parse(localStorage.getItem("user"));

    const { companyId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [view, setView] = useState("editcompany");
    const [editable, setEditable] = useState(company.companyId == "ADD" ? true : false);

    const [isnew, setIsNew] = useState(false);
    const [isProcess, setIsProcess] = useState(false);
    const [delCheckResult, setDelCheckResult] = useState({ userCount: 0, siteCount: 0, childCompanyCount: 0 });
    const [companyName, setCompanyName] = useState(company ? company.name : "");
    const [companies, setCompanies] = useState([]);
    const [userCombo, setUserCombo] = useState([]);
    const [serviceStatusEnabled, setServiceStatusEnabled] = useState(company.serviceStatusEnabled || false);
    // IMP-449: 기본 true(관계자 한정) — 필드 미존재(기존 회사 문서)도 켜짐으로 취급.
    const [commentMailLimitedRecipients, setCommentMailLimitedRecipients] = useState(company.serviceCommentLimitedRecipients !== false);

    useEffect(() => {
        // 직전에 본 회사(redux company)가 아니라 이번 조회가 끝난 뒤에만 본문을 보여 준다(성공·실패 모두 해제).
        let alive = true;
        Promise.resolve(GetCompanyInfo(companyId))
            .catch((e) => {
                console.warn('[AdminCompanyEdit] GetCompanyInfo failed', { screen: 'AdminCompanyEdit', companyId, reason: String(e?.message ?? e).slice(0, 200) });
            })
            .finally(() => { if (alive) setIsLoading(false); });
        getCompanyUsers();
        return () => { alive = false; };
    }, [])

    useEffect(() => {
        setServiceStatusEnabled(company.serviceStatusEnabled || false);
        setCommentMailLimitedRecipients(company.serviceCommentLimitedRecipients !== false);
    }, [company])

    // IMP-484: 회사 정보·연락처 카드는 머리글 잠금 해제가 곧 편집 시작이고 [저장]·[취소] 로 한 번에 저장한다.
    //   카드 둘이 한 잠금을 공유하므로 onClose 를 주지 않는다(한 카드 저장이 다른 카드 편집을 닫지 않게).
    const fieldNames = useMemo(() => buildCompanyFormControls({ company: {}, companyId: '', editable: false, authUser: userInfo, userCombo: [] })
        .map(card => card.items.map(item => item.name)), []);
    const toDraftOf = (idx) => (c) => fieldNames[idx].reduce((acc, n) => { acc[n] = c?.[n]; return acc; }, {});
    const saveChanged = (changed) => UpdateCompanyInfo(company.companyId, changed);
    const card0 = useBatchEdit({ source: company, toDraft: toDraftOf(0), save: saveChanged, editable, entry: 'header' });
    const card1 = useBatchEdit({ source: company, toDraft: toDraftOf(1), save: saveChanged, editable, entry: 'header' });

    // 조회가 실패하면 redux company 는 직전에 본 회사로 남는다. 경로의 companyId 와 정확히 같을 때만 본문·쓰기 컨트롤을 그린다.
    // (서버 GET /company/:companyId 가 companyId 완전 일치 조회라 === 비교)
    const isCurrent = !!company?.companyId && company.companyId === companyId;

    const isSu = isCompanySuperUser(userInfo);
    // 칸 편집 가능 여부는 카드별 editing 으로만 정한다(권한 조건은 buildCompanyFormControls 안에 그대로).
    const form_controls = [
        buildCompanyFormControls({ company: card0.draft, companyId, editable: card0.editing, authUser: userInfo, userCombo })[0],
        buildCompanyFormControls({ company: card1.draft, companyId, editable: card1.editing, authUser: userInfo, userCombo })[1],
    ];

    const getCompanyUsers = async () => {
        setUserCombo(await fetchBranchUserCombo());
    }

    return (
        <>
            {isLoading && <Loading />}
            {!isLoading && !isCurrent &&
                <>
                    <div className="extraHeader">
                        <h2 className="mb-0" />
                        <div className="ray-right">
                            <Link to="/Admin/AdminCompanyList" className="btn btn-default btn-sm mt-0 me-1" >{t('common.label.list')}</Link>
                        </div>
                    </div>
                    <div id="appCapsule" className="extra-header-active full-height">
                        <div className="section mt-1 mb-1"><p className="text-center">{t('common.message.noData')}</p></div>
                    </div>
                </>
            }
            {!isLoading && isCurrent &&
                <>
                    <div className="extraHeader">
                        <h2 className="mb-0">
                            {company.name}
                            {
                                view == "editcompany" &&
                                <a href onClick={() => setEditable(!editable)} className="btn btn-secondary btn-sm mt-0 ms-1">
                                    <ion-icon name={editable ? "lock-open-outline" : "lock-closed-outline"} />
                                    {editable ? t('common.status.unlocked') : t('common.status.locked')}
                                </a>}
                        </h2>
                        <div className="ray-right">
                            <SettingsReturnButton />
                            {company.companyId != "ADD" &&
                                <>
                                    {view != "editcompany" && <button className="btn btn-primary btn-sm mt-0 me-1" onClick={() => setView('editcompany')}>{t('company.label.companyInfo')}</button>}
                                    {view != "delcompanyconfirm" && <button className="btn btn-danger btn-sm mt-0 me-1" onClick={() => setView('delcompanyconfirm')}>{t('company.button.deleteCompany')}</button>}
                                </>
                            }
                            <Link to="/Admin/AdminCompanyList" className="btn btn-default btn-sm mt-0 me-1" >{t('common.label.list')}</Link>
                        </div>
                    </div>
                    <div id="appCapsule" className="extra-header-active full-height">
                        <div className="section mt-1 mb-1 ">
                        </div>
                        {view == "editcompany" &&
                            <>
                                {/* BUG-194 / IMP-415: su 전용 필드가 잠긴 사유를 명시.
                                    AdminUserEditInfo 의 tier 안내 배너와 동일 패턴. */}
                                {!isSu && (
                                    <div className="section mt-1 mb-2">
                                        <div className="alert alert-info py-2 small mb-0">
                                            🔒 {t('company.message.suOnlyFieldsReadOnly')}
                                        </div>
                                    </div>
                                )}
                                <div className="row">
                                    <div className="col-md-6">
                                        <RayForm key={card0.formKey} controls={[form_controls[0]]}
                                            handleChangeData={card0.onChange}
                                            errors={card0.errors} />
                                        <div className="ps-1 pe-1 mb-1">
                                            <EditSaveCancelBar canEdit={editable} editing={card0.editing} onEdit={card0.onEdit} onSave={() => card0.onSave([form_controls[0]])} onCancel={card0.onCancel} saving={card0.saving} />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <RayForm key={card1.formKey} controls={[form_controls[1]]}
                                            handleChangeData={card1.onChange}
                                            errors={card1.errors} />
                                        <div className="ps-1 pe-1 mb-1">
                                            <EditSaveCancelBar canEdit={editable} editing={card1.editing} onEdit={card1.onEdit} onSave={() => card1.onSave([form_controls[1]])} onCancel={card1.onCancel} saving={card1.saving} />
                                        </div>
                                        <div className="mt-2">
                                            <div className="card mb-2">
                                                <div className="card-header fw-bold d-flex align-items-center justify-content-between">
                                                    <span>{t('service.status.title')}</span>
                                                    <div style={{ display: 'inline-block', cursor: editable ? 'pointer' : 'default', opacity: editable ? 1 : 0.5 }}
                                                        onClick={() => {
                                                            if (!editable) return;
                                                            const next = !serviceStatusEnabled;
                                                            setServiceStatusEnabled(next);
                                                            UpdateCompanyInfo(company.companyId, { serviceStatusEnabled: next });
                                                        }}>
                                                        <div style={{
                                                            width: 44, height: 24, borderRadius: 12,
                                                            backgroundColor: serviceStatusEnabled ? '#00beff' : '#ccc',
                                                            position: 'relative', transition: 'background-color 0.2s'
                                                        }}>
                                                            <div style={{
                                                                width: 20, height: 20, borderRadius: '50%',
                                                                backgroundColor: '#fff', position: 'absolute', top: 2,
                                                                left: serviceStatusEnabled ? 22 : 2, transition: 'left 0.2s',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                            }} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-body py-2">
                                                    <small className="text-muted">
                                                        {serviceStatusEnabled
                                                            ? t('service.status.enabledDesc') || 'Service Status Badge is displayed in the header. Notification and Issue Manager settings are available below.'
                                                            : t('service.status.disabledDesc') || 'Service Status Badge is hidden. Enable to configure notifications and issue managers.'}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="card mb-2">
                                                <div className="card-header fw-bold d-flex align-items-center justify-content-between">
                                                    <span>{t('service.commentMail.limitedTitle')}</span>
                                                    <div data-testid="comment-mail-limited-toggle"
                                                        role="switch"
                                                        aria-checked={commentMailLimitedRecipients ? 'true' : 'false'}
                                                        style={{ display: 'inline-block', cursor: editable ? 'pointer' : 'default', opacity: editable ? 1 : 0.5 }}
                                                        onClick={() => {
                                                            if (!editable) return;
                                                            const next = !commentMailLimitedRecipients;
                                                            setCommentMailLimitedRecipients(next);
                                                            UpdateCompanyInfo(company.companyId, { serviceCommentLimitedRecipients: next });
                                                        }}>
                                                        <div style={{
                                                            width: 44, height: 24, borderRadius: 12,
                                                            backgroundColor: commentMailLimitedRecipients ? '#00beff' : '#ccc',
                                                            position: 'relative', transition: 'background-color 0.2s'
                                                        }}>
                                                            <div style={{
                                                                width: 20, height: 20, borderRadius: '50%',
                                                                backgroundColor: '#fff', position: 'absolute', top: 2,
                                                                left: commentMailLimitedRecipients ? 22 : 2, transition: 'left 0.2s',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                            }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {serviceStatusEnabled &&
                                                <IssueManagerSection
                                                    companyId={company.companyId}
                                                    editable={editable}
                                                    userCombo={userCombo}
                                                />
                                            }
                                            <CompanyWorkScheduleSection
                                                companyId={company.companyId}
                                                editable={editable}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                        {view == "delcompanyconfirm" &&
                            <div className="section ">
                                <AdminCompanyDelete company={company} />
                            </div>
                        }
                    </div>
                </>
            }
        </>
    );
};

const mapState = (state) => {
    const company = state.CompanyReducer.company;
    return { company };
}

const mapDispatch = dispatch => ({
    DeleteCompany: (companyid) => dispatch(CompanyAction.DeleteCompany(companyid)),
    UpdateCompanyInfo: (companyid, data) => dispatch(CompanyAction.UpdateCompanyInfo(companyid, data)),
    GetCompanyInfo: (companyId) => dispatch(CompanyAction.GetCompanyInfo(companyId)),
})

export default connect(mapState, mapDispatch)(AdminCompanyEdit);
